'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import type { SiteSearchResult } from '@/lib/site-search';
import { MIN_QUERY_LENGTH } from '@/lib/site-search';

/** Debounce so typing doesn't fire a request per keystroke. */
const DEBOUNCE_MS = 250;

export default function NotFoundSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SiteSearchResult[]>([]);
  const [state, setState] = useState<'idle' | 'searching' | 'done'>('idle');
  // Guards against out-of-order responses overwriting a newer query's results.
  const latest = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setState('idle');
      return;
    }

    setState('searching');
    const seq = ++latest.current;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/site-search?q=${encodeURIComponent(q)}`, {
          signal: controller.signal,
        });
        const data = res.ok ? await res.json() : [];
        if (seq !== latest.current) return; // a newer query already won
        setResults(Array.isArray(data) ? data : []);
        setState('done');
      } catch {
        if (seq !== latest.current) return;
        setResults([]);
        setState('done');
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const showEmpty = state === 'done' && results.length === 0 && query.trim().length >= MIN_QUERY_LENGTH;

  return (
    <div className="w-full max-w-xl">
      <label htmlFor="nf-search" className="sr-only">
        Search the site
      </label>
      <div className="relative">
        <svg
          className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/60"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
        </svg>
        <input
          id="nf-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for a product, guide or article…"
          autoComplete="off"
          className="w-full rounded-full border border-white/25 bg-white/10 py-3.5 pl-12 pr-4 text-white placeholder-white/60 backdrop-blur-sm outline-none transition focus:border-white/50 focus:bg-white/15"
        />
      </div>

      {(results.length > 0 || showEmpty) && (
        <div className="mt-3 overflow-hidden rounded-2xl bg-white text-left shadow-2xl">
          {showEmpty ? (
            <p className="px-5 py-4 text-sm" style={{ color: 'var(--color-t-body)' }}>
              No matches for “{query.trim()}”. Try a broader term, or pick a section below.
            </p>
          ) : (
            // Cap the height and scroll: up to 20 results (5 per collection) would
            // otherwise run off the bottom of the viewport. The panel stays in normal
            // flow rather than overlaying, because the hero section is overflow-hidden
            // and would clip an absolutely-positioned dropdown.
            // overscroll-contain stops the page scrolling once the list bottoms out.
            <ul
              role="list"
              className="max-h-[min(60vh,24rem)] divide-y divide-gray-100 overflow-y-auto overscroll-contain"
            >
              {results.map((r) => (
                <li key={`${r.kind}-${r.url}`}>
                  <Link
                    href={r.url}
                    className="flex items-start gap-3 px-5 py-3.5 transition hover:bg-gray-50"
                  >
                    <span
                      className="mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white"
                      style={{ backgroundColor: 'var(--color-t-primary)' }}
                    >
                      {r.label}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-semibold" style={{ color: 'var(--color-t-heading)' }}>
                        {r.title}
                      </span>
                      {r.excerpt && (
                        <span className="mt-0.5 block truncate text-sm" style={{ color: 'var(--color-t-body)' }}>
                          {r.excerpt}
                        </span>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
